import { useTranslation } from 'react-i18next'
import {
  PARTNER_LOGOS,
  VALUES_CAROUSEL,
  BEYOND_CLASSROOM_SLIDES,
  REAL_WORLD_PROGRAMS,
  MIS_KHALDA_GALLERY,
} from '../../data/siteContent'
import AppDownloadBadges from './AppDownloadBadges'
import LogoGrid from './LogoGrid'
import CommunityPartnershipsHero from './CommunityPartnershipsHero'
import UniversityDestinationsSection from './UniversityDestinationsSection'
import CampusPhotoGallery from './CampusPhotoGallery'
import ContentCarousel, { CarouselLink } from './ContentCarousel'
import RoadAheadSection from './RoadAheadSection'
import Breadcrumbs from './Breadcrumbs'
import Button from '../ui/Button'
import CampusTourCta from './CampusTourCta'
import ChoosingProgramCta from './ChoosingProgramCta'
import MediaImage from '../ui/MediaImage'
import { resolveMediaUrl } from '../../utils/media'

export default function PageExtras({ slug }) {
  const { t } = useTranslation()

  switch (slug) {
    case 'CommunityPartnerships':
      return (
        <>
          <CommunityPartnershipsHero />
          <LogoGrid logos={PARTNER_LOGOS} title={t('pagesExtras.partnersTitle')} />
        </>
      )

    case 'ValuesCharacter':
      return (
        <ContentCarousel
          items={VALUES_CAROUSEL}
          className="my-10"
          renderSlide={(item) => (
            <div className="grid gap-6 p-6 md:grid-cols-2 md:p-8">
              <MediaImage
                src={resolveMediaUrl(item.image)}
                alt={t(`pagesExtras.values.${item.value}.title`)}
                className="h-56 w-full rounded-xl object-cover md:h-full"
              />
              <div className="flex flex-col justify-center">
                <h3 className="mb-2 text-xl font-bold text-primary">
                  {t(`pagesExtras.values.${item.value}.title`)}
                </h3>
                <blockquote className="border-s-4 border-accent ps-4 italic text-text/75">
                  {t(`pagesExtras.values.${item.value}.quote`)}
                </blockquote>
              </div>
            </div>
          )}
        />
      )

    case 'BeyondClassroom':
      return (
        <ContentCarousel
          items={BEYOND_CLASSROOM_SLIDES}
          dark
          className="my-10"
          renderSlide={(item) => (
            <div className="grid gap-6 p-6 md:grid-cols-2 md:p-8">
              <MediaImage src={resolveMediaUrl(item.image)} alt="" className="h-56 w-full rounded-xl object-cover" />
              <div className="flex flex-col justify-center text-white">
                <h3 className="mb-3 text-xl font-bold">{t(`pagesExtras.beyond.${item.key}.title`)}</h3>
                <p className="mb-4 text-white/80">{t(`pagesExtras.beyond.${item.key}.text`)}</p>
                <CarouselLink to={item.link}>{t('pagesExtras.exploreSchools')}</CarouselLink>
              </div>
            </div>
          )}
        />
      )

    case 'RealWorldReadiness':
      return (
        <ContentCarousel
          items={REAL_WORLD_PROGRAMS}
          dark
          className="my-10"
          renderSlide={(item) => (
            <div className="grid gap-6 p-6 md:grid-cols-2 md:p-8">
              <MediaImage src={resolveMediaUrl(item.image)} alt="" className="h-56 w-full rounded-xl object-cover" />
              <div className="flex flex-col justify-center text-white">
                <h3 className="mb-3 text-xl font-bold">{t(`pagesExtras.realWorld.${item.key}.title`)}</h3>
                <p className="text-white/80">{t(`pagesExtras.realWorld.${item.key}.text`)}</p>
              </div>
            </div>
          )}
        />
      )

    case 'StudentExperiences':
      return (
        <div className="my-10 rounded-2xl border border-border bg-background p-6 sm:p-8">
            <h3 className="text-xl font-bold text-primary">
              {t('pagesExtras.studentExperiences.beyondTitle')}
            </h3>
            <p className="mt-3 leading-relaxed text-text/70">
              {t('pagesExtras.studentExperiences.beyondText')}
            </p>
            <Button to="/pages/BeyondClassroom" variant="accent" className="mt-5">
              {t('pagesExtras.studentExperiences.beyondCta')}
            </Button>
          </div>
      )

    case 'UniversityCareerPathways':
      return <UniversityDestinationsSection />

    case 'MisKhalda':
      return (
        <>
          <CampusPhotoGallery
            images={MIS_KHALDA_GALLERY}
            label={t('pagesExtras.misKhaldaGallery.label')}
            title={t('pagesExtras.misKhaldaGallery.title')}
            subtitle={t('pagesExtras.misKhaldaGallery.subtitle')}
          />
          <CampusTourCta campus="MisKhalda" />
        </>
      )

    case 'MisTlaAlAli':
      return <CampusTourCta campus="MisTlaAlAli" />

    case 'StudentSafety':
      return (
        <div className="my-10 flex flex-wrap gap-4">
          <a
            href="/documents/StudentSafety-policy-en.pdf"
            className="rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary hover:border-accent"
          >
            {t('pagesExtras.safetyPolicyEn')}
          </a>
          <a
            href="/documents/StudentSafety-policy-ar.pdf"
            className="rounded-lg border border-border px-4 py-3 text-sm font-semibold text-primary hover:border-accent"
          >
            {t('pagesExtras.safetyPolicyAr')}
          </a>
        </div>
      )

    case 'MayarApp':
      return <AppDownloadBadges />

    case 'PathwaysChoices':
      return (
        <>
          <Breadcrumbs
            items={[
              { label: t('brand.shortName'), to: '/' },
              { label: t('pagesExtras.pathwaysTitle') },
            ]}
          />
          <blockquote className="my-6 rounded-2xl border border-border bg-background p-6 text-center italic text-primary">
            {t('pagesExtras.pathwaysQuote')}
            <footer className="mt-3 text-sm not-italic font-semibold text-text/60">
              Dr. Nadia Kharbat — {t('pagesExtras.generalDirector')}
            </footer>
          </blockquote>
          <Button to="/counseling" variant="accent">
            {t('pagesExtras.bookCounseling')}
          </Button>
        </>
      )

    case 'ChoosingProgram':
      return (
        <>
          <ChoosingProgramCta />
          <RoadAheadSection />
        </>
      )

    case 'ServiceCounseling':
      return (
        <div className="my-10 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-accent/30 bg-accent/5 p-6">
          <div>
            <p className="text-sm font-bold uppercase tracking-widest text-accent">{t('counseling.label')}</p>
            <h3 className="mt-2 text-lg font-bold text-primary">{t('pagesExtras.bookCounseling')}</h3>
          </div>
          <Button to="/counseling" variant="accent">
            {t('pagesExtras.bookCounseling')}
          </Button>
        </div>
      )

    case 'WellbeingCounseling':
      return (
        <div className="my-10 rounded-2xl border border-accent/30 bg-accent/5 p-6">
          <p className="text-sm font-bold uppercase tracking-widest text-accent">{t('pagesExtras.spotlight')}</p>
          <h3 className="mt-2 text-lg font-bold text-primary">{t('pagesExtras.wellbeingSpotlight.title')}</h3>
          <p className="mt-2 text-text/70">{t('pagesExtras.wellbeingSpotlight.text')}</p>
        </div>
      )

    default:
      return null
  }
}
